import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Tab } from '@hologram-dimension/tab';
import useDevice from 'api/legacy/devices/useDevice';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import useSimBilling from 'api/sims/useSimBilling';
import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useSimDeviceOverview from 'api/sims/useSimDeviceOverview';
import useSimFallbackHistory from 'api/sims/useSimFallbackHistory';
import useSimMostRecentSession from 'api/sims/useSimMostRecentSession';
import useSimProfileHistory from 'api/sims/useSimProfileHistory';
import useSimProfiles from 'api/sims/useSimProfiles';
import useSimSmsCount from 'api/sims/useSimSmsCount';
import { clsx } from 'clsx/lite';
import SimCoveragePanel from 'components/SimCoveragePanel/SimCoveragePanel';
import SimDetailsHeader from 'components/SimDetailsHeader/SimDetailsHeader';
import useIsDeactivatedOldSim from 'hooks/SimDetails/useIsDeactivatedOldSim';
import useUserInfo from 'hooks/User/useUserInfo';
import PurchasePhoneNumber from 'modals/PhoneNumber/PurchasePhoneNumber/PurchasePhoneNumber';
import RemovePhoneNumber from 'modals/PhoneNumber/RemovePhoneNumber/RemovePhoneNumber';
import sharedPageStyles from 'pages/sharedPage.module.css';
import {
  type ComponentType,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import OrganizationProvider from 'state/contexts/Organization/OrganizationProvider';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { SWRConfig, SWRConfiguration } from 'swr';
import type {
  SimsDaoDeviceDetails,
  SimsDaoDeviceOverview,
} from 'types/__generated__/SimInventoryApi';
import {
  type OnSimsModified,
  type OpenModalCallback,
  type SimDetailsView,
  SimDetailsViews,
} from 'types/common';
import { useUrl } from 'utils/hooks';
import useSimUrl from 'utils/hooks/useSimUrl';
import SimAndDevicesView from 'views/SimDetails/SimAndDevicesView/SimAndDevicesView';
import SimPricingView from 'views/SimDetails/SimPricing/SimPricingView';
import SimProfilesView from 'views/SimDetails/SimProfilesView/SimProfilesView';
import SimStatusView from 'views/SimDetails/SimStatusView/SimStatusView';
import SimUsageView from 'views/SimDetails/SimUsage/SimUsage';
import Webhooks from 'views/SimDetails/Webhooks/Webhooks';
import SimRecentSessionsView from 'views/SimRecentSessionsView/SimRecentSessionsView';
import useBalance from 'api/legacy/organization/useBalance';
import CustomPostpaidDisclaimer from 'components/CustomPostpaidDisclaimer/CustomPostpaidDisclaimer';
import styles from './SimDetails.module.css';

interface SimDetailsPageProps {
  className?: string;
  hologramSimId?: HologramSimId;
  viewKey: SimDetailsView;
  onNavigate: (url: string) => void;
  handleModalOpen: OpenModalCallback;
  simDetailBaseUrl: string;
  onDeviceChange: (newDevice?: SimsDaoDeviceOverview & SimsDaoDeviceDetails) => void;
  simInventoryBaseUrl: string;
  RoutesComponent: ComponentType;
  usageByRecentSessionUrl: string;
  dataPlanUrl: string;
  usageExitedTestingUrl: string;
}

const simInventorySwrConfig: SWRConfiguration = {
  dedupingInterval: 60000,
  focusThrottleInterval: 300000,
  loadingTimeout: 3000,
  errorRetryInterval: 10000,
  errorRetryCount: 3,
};

// This inner wrapper needed to make sure SWR config is wrapped
const SimDetailsPageInner = forwardRef<OnSimsModified, SimDetailsPageProps>(
  (
    {
      className,
      hologramSimId,
      viewKey,
      onNavigate,
      simDetailBaseUrl,
      handleModalOpen,
      onDeviceChange,
      simInventoryBaseUrl,
      RoutesComponent,
      usageByRecentSessionUrl,
      dataPlanUrl,
      usageExitedTestingUrl,
    },
    ref
  ) => {
    const {
      deviceOverview,
      mutate: mutateSimAndDeviceOverview,
      isLoading: overviewLoading,
    } = useSimDeviceOverview(hologramSimId);
    const {
      simDetails,
      mutate: mutateSimDetails,
      isLoading: simDetailsLoading,
      error: simDetailsError,
    } = useSimDetails(hologramSimId);
    const {
      deviceDetails,
      isLoading: simDeviceDetailsLoading,
      mutate: mutateSimDeviceDetails,
    } = useSimDeviceDetails(hologramSimId);
    const {
      simProfiles,
      error: simProfilesError,
      isLoading: simProfilesIsLoading,
      mutate: mutateProfiles,
    } = useSimProfiles(hologramSimId);
    const { isDeactivatedOldSim, isLoading: deactivatedOldSimLoading } = useIsDeactivatedOldSim({
      hologramSimId,
    });
    const { balance, isLoading: isBalanceLoading } = useBalance(simDetails.organizationId);

    // TODO: Remove when we have real connected status
    const { isLoading: deviceLoading, mutate: mutateDevice } = useDevice(
      simDetails.organizationId && deviceDetails.claimedDeviceId
        ? deviceDetails.claimedDeviceId
        : undefined,
      simDetails.organizationId
    );

    const { isSuperAdmin } = useUserInfo();

    const { isLoading: openSessionsLoading, mutate: mutateOpenSessions } = useOpenSessions(
      simDetails.organizationId,
      isSuperAdmin || !!simDetails.organizationId
    );

    // The following are triggered to enable smooth response when navigating and for refresh
    const { mutate: mutateProfileHistory } = useSimProfileHistory(hologramSimId);
    const { mutate: mutateFallbackHistory } = useSimFallbackHistory(hologramSimId);
    const { mutate: mutateMostRecentSession } = useSimMostRecentSession(hologramSimId);
    const { mutate: mutateBilling } = useSimBilling(hologramSimId);
    const { mutate: mutateSimSmsCount } = useSimSmsCount(hologramSimId);

    const switchingToProfile =
      !simProfilesIsLoading &&
      !simProfilesError &&
      simProfiles?.find((profile) => profile.installationState === 'ENABLE_PENDING');

    // TODO: Remove once nav is in hyper dashboard
    useEffect(() => {
      onDeviceChange({ ...deviceOverview, ...deviceDetails });
    }, [deviceDetails, deviceOverview, onDeviceChange]);

    // TODO: Switch to global key-based mutate: https://swr.vercel.app/docs/mutation#global-mutate
    const onSimsModified = useCallback<OnSimsModified>(() => {
      mutateSimAndDeviceOverview();
      mutateSimDetails();
      mutateSimDeviceDetails();
      mutateMostRecentSession();
      mutateProfiles();
      mutateFallbackHistory();
      mutateProfileHistory();
      mutateBilling();
      mutateSimSmsCount();
      mutateOpenSessions();
      mutateDevice();
    }, [
      mutateBilling,
      mutateDevice,
      mutateFallbackHistory,
      mutateMostRecentSession,
      mutateOpenSessions,
      mutateProfileHistory,
      mutateProfiles,
      mutateSimAndDeviceOverview,
      mutateSimDetails,
      mutateSimDeviceDetails,
      mutateSimSmsCount,
    ]);

    useImperativeHandle(ref, () => onSimsModified, [onSimsModified]);

    const userOrgId = useOrganization();
    // Organization ID for a SIM trumps logged in org ID
    const simOrgId = simDetails.organizationId;

    const url = useSimUrl({ simDetailBaseUrl, orgId: simOrgId, simId: hologramSimId });
    const handleTabClick = useCallback(
      (view: string) => {
        onNavigate(view === 'details' ? (url ?? '') : `${url}/${view}`);
      },
      [onNavigate, url]
    );

    const activeSimsUrl = useUrl({
      url: `${simInventoryBaseUrl}/active`,
      replacers: [{ placeholder: 'orgid', value: userOrgId?.toString() ?? '' }],
    });

    // SIM Details modals. TODO: Consider a hook/context to manage open modals? We allow layering them, but perhaps keeping them local is simpler?
    // Or just move down to the views that need them?
    const [addPhoneNumberModalOpen, setPurchasePhoneNumberModalOpen] = useState(false);
    const [removePhoneNumberModalOpen, setRemovePhoneNumberModalOpen] = useState(false);

    const openPurchasePhoneNumberModal = useCallback(
      () => setPurchasePhoneNumberModalOpen(true),
      []
    );
    const openRemovePhoneNumberModal = useCallback(() => setRemovePhoneNumberModalOpen(true), []);
    const closePurchasePhoneNumberModal = useCallback(
      () => setPurchasePhoneNumberModalOpen(false),
      []
    );
    const closeRemovePhoneNumberModal = useCallback(() => setRemovePhoneNumberModalOpen(false), []);

    const { organizationRole, releaseFlags } = useUserInfo();
    const allowPricing =
      ['admin', 'manager', 'editor'].includes(organizationRole) && !isDeactivatedOldSim;

    const hasRoutes = releaseFlags?.has(ReleaseFlag.FEATURE_ROUTES) && !isDeactivatedOldSim;
    const hasWebhooks = releaseFlags?.has(ReleaseFlag.FEATURE_WEBHOOKS) && !isDeactivatedOldSim;
    const showLocations =
      !releaseFlags?.has(ReleaseFlag.HIDE_TOWER_LOCATIONS) && !isDeactivatedOldSim;
    const showCoverage = !isDeactivatedOldSim;
    const showRecentSessions = !isDeactivatedOldSim;

    const bannerNotifications: Array<ReactNode> = useMemo(() => {
      const notifications: React.ReactElement[] = [];

      if (switchingToProfile) {
        notifications.push(
          <BannerNotification prefixOverride="In progress" variant="note">
            A request to switch to the {switchingToProfile.profileName} profile has been issued to
            our carrier partners. This request will take effect the next time your device connects
            to a network. Connecting should take less than 10 minutes.
          </BannerNotification>
        );
      }
      return notifications;
    }, [switchingToProfile]);

    // TOOD: Temporary workaround to just putting the "content" into an internal component
    const content = useMemo(() => {
      const head = (
        <Helmet>
          <title>{`${deviceOverview.deviceName ?? 'SIM Details'} - Hologram`}</title>
        </Helmet>
      );

      if (
        overviewLoading ||
        simDetailsLoading ||
        simDeviceDetailsLoading ||
        openSessionsLoading ||
        deviceLoading ||
        deactivatedOldSimLoading ||
        isBalanceLoading
      ) {
        return (
          <>
            {head}
            <Loader className={styles.loader}>Loading SIM details...</Loader>
          </>
        );
      }

      if (simDetailsError) {
        // typically when user session times out
        return (
          <PanelNotification
            icon="CircleAlert"
            label="We weren’t able to load this SIM"
            actions={
              <Button variant="secondary" to={activeSimsUrl ?? '/'}>
                View active SIMs
              </Button>
            }
          >
            You can try reloading the page, looking in the SIMs pages, or searching.
          </PanelNotification>
        );
      }

      if (!simDetails.hologramSimId) {
        // object returns empty if the SIM ID is invalid
        return (
          <PanelNotification
            icon="CircleAlert"
            label="No matching SIM found"
            actions={
              <Button variant="secondary" to={activeSimsUrl ?? '/'}>
                View active SIMs
              </Button>
            }
          >
            Check that the address or IDs you are searching for are correct.
          </PanelNotification>
        );
      }

      return (
        <>
          {head}
          {/* TODO: Refactor to use BorderlessPanel, this gets us better scrolling AND the header stays pinned.
                May also prevent problems with the view jumping as scrollbars appear and disappear */}
          <div className={styles.headerWrapper}>
            <SimDetailsHeader
              hologramSimId={hologramSimId}
              onSimsModified={onSimsModified}
              handleModalOpen={handleModalOpen}
              simDetailBaseUrl={simDetailBaseUrl}
            />
            <hr className={styles.divider} />
            <div className={sharedPageStyles.tabWrapper}>
              {Object.entries(SimDetailsViews)
                .filter(([view]) => {
                  // TODO: Refactor to use a map of views to roles/feature flags (AI suggested this, I kind of like it)
                  if (view === 'routes') {
                    return hasRoutes;
                  }
                  if (view === 'webhooks') {
                    return hasWebhooks;
                  }
                  if (view === 'pricing') {
                    return allowPricing;
                  }
                  if (view === 'locationhistory') {
                    return showLocations;
                  }
                  if (view === 'coverage') {
                    return showCoverage;
                  }
                  if (view === 'recentsessions') {
                    return showRecentSessions;
                  }
                  return true;
                })
                .map(([view, viewName]) => (
                  <Tab
                    id={`simdetails-tab-${view}`}
                    key={view}
                    selected={view === viewKey}
                    onClick={() => handleTabClick(view)}
                  >
                    {viewName}
                  </Tab>
                ))}
            </div>
          </div>
          <div
            className={clsx(
              sharedPageStyles.viewsWrapper,
              viewKey === 'usage' && styles.embeddedReportView
            )}
          >
            <div className={styles.viewContent}>
              {bannerNotifications.length > 0 && (
                <div className={styles.bannerNotifications}>{bannerNotifications}</div>
              )}
              {viewKey === 'details' && (
                <SimStatusView
                  hologramSimId={hologramSimId}
                  simDetailBaseUrl={simDetailBaseUrl}
                  usageExitedTestingUrl={usageExitedTestingUrl}
                />
              )}
              {viewKey === 'profiles' && (
                <SimProfilesView
                  hologramSimId={hologramSimId}
                  simDetailBaseUrl={simDetailBaseUrl}
                />
              )}
              {viewKey === 'simanddevices' && (
                <SimAndDevicesView
                  hologramSimId={hologramSimId}
                  simDetailBaseUrl={simDetailBaseUrl}
                  handleModalOpen={handleModalOpen}
                  onSimsModified={onSimsModified}
                />
              )}
              {viewKey === 'recentsessions' && (
                <SimRecentSessionsView
                  hologramSimId={hologramSimId}
                  simDetailBaseUrl={simDetailBaseUrl}
                  pageSize={100}
                  mode="sim"
                  usageByRecentSessionUrl={usageByRecentSessionUrl}
                />
              )}
              {viewKey === 'usage' && <SimUsageView hologramSimId={hologramSimId} />}
              {viewKey === 'pricing' && allowPricing && (
                <>
                  {balance?.billing_version === 3 && <CustomPostpaidDisclaimer />}
                  <SimPricingView
                    hologramSimId={hologramSimId}
                    simDetailBaseUrl={simDetailBaseUrl}
                    dataPlanUrl={dataPlanUrl}
                    orgId={simOrgId}
                    handleModalOpen={handleModalOpen}
                    onSimsModified={onSimsModified}
                    openPurchasePhoneNumberModal={openPurchasePhoneNumberModal}
                    openRemovePhoneNumberModal={openRemovePhoneNumberModal}
                  />
                </>
              )}
              {viewKey === 'coverage' && showCoverage && (
                <>
                  {balance?.billing_version === 3 && <CustomPostpaidDisclaimer />}
                  <SimCoveragePanel hologramSimId={hologramSimId} />
                </>
              )}
              {viewKey === 'webhooks' && hasWebhooks && <Webhooks hologramSimId={hologramSimId} />}
              {viewKey === 'routes' && hasRoutes && <RoutesComponent />}
            </div>
          </div>
          {addPhoneNumberModalOpen && (
            <PurchasePhoneNumber
              hologramSimId={hologramSimId}
              deviceId={deviceDetails.claimedDeviceId}
              onConfirmed={closePurchasePhoneNumberModal}
              onCancelled={closePurchasePhoneNumberModal}
            />
          )}
          {removePhoneNumberModalOpen && (
            <RemovePhoneNumber
              hologramSimId={hologramSimId}
              deviceId={deviceDetails.claimedDeviceId}
              onConfirmed={closeRemovePhoneNumberModal}
              onCancelled={closeRemovePhoneNumberModal}
            />
          )}
        </>
      );
    }, [
      deviceOverview.deviceName,
      overviewLoading,
      simDetailsLoading,
      simDeviceDetailsLoading,
      openSessionsLoading,
      deviceLoading,
      deactivatedOldSimLoading,
      simDetailsError,
      simDetails.hologramSimId,
      hologramSimId,
      onSimsModified,
      handleModalOpen,
      simDetailBaseUrl,
      bannerNotifications,
      viewKey,
      usageByRecentSessionUrl,
      allowPricing,
      simOrgId,
      openPurchasePhoneNumberModal,
      openRemovePhoneNumberModal,
      hasWebhooks,
      hasRoutes,
      RoutesComponent,
      addPhoneNumberModalOpen,
      deviceDetails.claimedDeviceId,
      closePurchasePhoneNumberModal,
      removePhoneNumberModalOpen,
      closeRemovePhoneNumberModal,
      activeSimsUrl,
      showLocations,
      showCoverage,
      handleTabClick,
      dataPlanUrl,
      showRecentSessions,
      usageExitedTestingUrl,
      isBalanceLoading,
      balance?.billing_version,
    ]);

    return (
      <OrganizationProvider organizationId={simOrgId}>
        <div className={clsx(sharedPageStyles.pageWrapper, styles.wrapper, className)}>
          {content}
        </div>
      </OrganizationProvider>
    );
  }
);

const SimDetailsPage = forwardRef<OnSimsModified, SimDetailsPageProps>((props, ref) => (
  <SWRConfig value={simInventorySwrConfig}>
    <SimDetailsPageInner ref={ref} {...props} />
  </SWRConfig>
));

export default SimDetailsPage;
